import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

// Social media links for floating icons (same as Music page)
const socialMediaLinks = [
  {
    name: "TikTok",
    url: "https://www.tiktok.com/@itsyungdexy04",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    color: "#000000",
  },
  {
    name: "Instagram",
    url: "https://www.instagram.com/itsyungdexy/",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    color: "#E4405F",
  },
  {
    name: "YouTube",
    url: "https://www.youtube.com/@itsyungdexy",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    color: "#FF0000",
  },
];

// Floating Social Icon Component
const FloatingSocialIcon = ({ social, index }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    // Random starting position
    const startX = Math.random() * 100;
    const startY = 20 + Math.random() * 60;
    setPosition({ x: startX, y: startY });
    
    // Animation loop
    const animate = () => {
      setPosition(prev => ({
        x: prev.x + (Math.sin(Date.now() / 2000 + index) * 0.3),
        y: prev.y + (Math.cos(Date.now() / 3000 + index) * 0.2),
      }));
    };
    
    const interval = setInterval(animate, 50);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed z-40 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:scale-125 transition-all duration-300 shadow-lg"
      style={{
        left: `${Math.min(Math.max(position.x, 5), 90)}%`,
        top: `${Math.min(Math.max(position.y, 15), 85)}%`,
        color: social.color,
      }}
      title={social.name}
    >
      {social.icon}
    </a>
  );
};

const ShopPage = () => {
  const { category } = useParams();
  const { products, loading } = useCart();
  const { formatPrice, currency, setCurrency, availableCurrencies } = useCurrency();
  const [sortBy, setSortBy] = useState('featured');
  const [filterType, setFilterType] = useState(category || 'all');

  // Filter products based on category
  let filteredProducts = [...products];
  
  if (filterType === 'tshirts' || category === 'tshirts') {
    filteredProducts = products.filter(p => p.type === 'tshirt');
  } else if (filterType === 'hoodies' || category === 'hoodies') {
    filteredProducts = products.filter(p => p.type === 'hoodie');
  }

  // Default sort: tees first, then hoodies (by type then name)
  if (sortBy === 'featured') {
    filteredProducts.sort((a, b) => {
      // Tees before hoodies
      if (a.type === 'tshirt' && b.type === 'hoodie') return -1;
      if (a.type === 'hoodie' && b.type === 'tshirt') return 1;
      // Same type: sort by name
      return a.name.localeCompare(b.name);
    });
  } else if (sortBy === 'price-low') {
    filteredProducts.sort((a, b) => a.price - b.price);
  } else if (sortBy === 'price-high') {
    filteredProducts.sort((a, b) => b.price - a.price);
  } else if (sortBy === 'name') {
    filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
  }

  const getPageTitle = () => {
    if (filterType === 'tshirts' || category === 'tshirts') return 'T-SHIRTS';
    if (filterType === 'hoodies' || category === 'hoodies') return 'HOODIES';
    return 'ALL PRODUCTS';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white/60">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] relative">
      <AnnouncementBar />
      <Header />
      
      {/* Floating Social Media Icons */}
      {socialMediaLinks.map((social, index) => (
        <FloatingSocialIcon key={social.name} social={social} index={index} />
      ))}
      
      <main className="py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Breadcrumb */}
          <nav className="text-sm mb-8">
            <Link to="/" className="text-white/50 hover:text-white/70">Home</Link>
            <span className="mx-2 text-white/30">/</span>
            <span className="text-white">{getPageTitle()}</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{getPageTitle()}</h1>
          <p className="text-white/60 mb-8">{filteredProducts.length} products</p>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-8 pb-8 border-b border-white/20">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/10 border border-white/20 text-white px-4 py-2 text-sm cursor-pointer hover:border-white/40 transition-colors rounded"
            >
              <option value="featured" className="bg-[#1a1a1a]">Sort by: Featured</option>
              <option value="price-low" className="bg-[#1a1a1a]">Price: Low to High</option>
              <option value="price-high" className="bg-[#1a1a1a]">Price: High to Low</option>
              <option value="name" className="bg-[#1a1a1a]">Name: A-Z</option>
            </select>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-white/10 border border-white/20 text-white px-4 py-2 text-sm cursor-pointer hover:border-white/40 transition-colors rounded"
            >
              <option value="all" className="bg-[#1a1a1a]">All Categories</option>
              <option value="tshirts" className="bg-[#1a1a1a]">T-Shirts ({formatPrice(30)})</option>
              <option value="hoodies" className="bg-[#1a1a1a]">Hoodies ({formatPrice(39.99)})</option>
            </select>
            <select 
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="bg-white/10 border border-white/20 text-white px-4 py-2 text-sm cursor-pointer hover:border-white/40 transition-colors rounded"
              data-testid="currency-selector"
            >
              {availableCurrencies.map(curr => (
                <option key={curr} value={curr} className="bg-[#1a1a1a]">{curr}</option>
              ))}
            </select>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square overflow-hidden bg-white/10 rounded-lg mb-4">
                  <img
                    src={product.images?.[0]}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <p className="text-xs text-white/50 mb-1">Yung World</p>
                <h3 className="text-sm font-medium text-white group-hover:text-white/80">
                  {product.name} - {product.color}
                </h3>
                <p className="mt-1 text-sm font-bold text-white">
                  {formatPrice(product.price)}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
